import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import MiniSearch from 'minisearch';
import Navbar from '../components/Navbar';
import CourseTable from '../components/CourseTable';
import CourseFilters from '../components/CourseFilters';
import subjectMappingsData from '../data/subjectMappings.json';
import { createApiUrl } from '../config/api';
import { useToast } from '../context/ToastContext';
import { useCalendar } from '../context/CalendarContext';
import FloatingCalendarWidget from '../components/FloatingCalendarWidget';

// Pagination constant
const PAGE_SIZE = 50;

// Define stop words to be ignored in search
const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'of', 'for', 'in', 'on', 'at', 'to',
  'with', 'by', 'about', 'as', 'into', 'like', 'is', 'are', 'was', 'were',
]);

function Courses() {
  const { showToast } = useToast();
  const { addCourse, plannedCourses } = useCalendar();
  const [showCalendarWidget, setShowCalendarWidget] = useState(false);
  const [courses, setCourses] = useState([]);
  const [uniqueCourses, setUniqueCourses] = useState([]); // Deduplicated courses
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isDeduplicationDone, setIsDeduplicationDone] = useState(false);
  
  // Debug state - FIXED: Prevent infinite loops
  const [debugInfo, setDebugInfo] = useState({});
  const logDebug = (category, data) => {
    console.log(`[CourseSearch:${category}]`, data);
    // CRITICAL FIX: Don't call setState in handleFilterChange to prevent loops
    if (category !== 'FILTER_CHANGE') {
      setDebugInfo(prev => ({ ...prev, [category]: data }));
    }
  };

  // Search and Filter state
  const [globalFilter, setGlobalFilter] = useState('');
  const [debouncedGlobalFilter, setDebouncedGlobalFilter] = useState('');
  const [searchTime, setSearchTime] = useState(null);
  const searchInputRef = useRef(null);

  // MiniSearch state
  const [searchIndex, setSearchIndex] = useState(null);
  const [filteredCourses, setFilteredCourses] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Filter state
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [subjectMapping, setSubjectMapping] = useState(new Map());
  const [termOptions] = useState(['2025 Fall', '2026 Spring']);
  const [activeFilters, setActiveFilters] = useState({
    term: [],
    subject: [],
    showOnlyQRatings: false,
    showOnlyScheduled: false,
    sortByWorkload: 'none',
    sortByQScore: 'none',
    days: {
      M: false,
      T: false,
      W: false,
      Th: false,
      F: false,
    },
  });

  // Initialize MiniSearch instance
  useEffect(() => {
    const index = new MiniSearch({
      fields: [
        'class_tag',
        'class_name',
        'schedule',
        'instructors',
        'term',
        'mean_hours',
        'overall_score_course_mean',
      ],
      storeFields: [
        'class_tag',
        'class_name',
        'schedule',
        'instructors',
        'term',
        'mean_hours',
        'overall_score_course_mean',
      ],
      idField: '_id',
      extractField: (document, fieldName) => {
        if (fieldName === '_id') {
          // Create compound key from course_id and class_number (Classiq's exact approach)
          return `${document.course_id}-${document.class_number}`;
        }
        const value = document[fieldName];
        return value === null || value === undefined ? '' : String(value);
      },
      searchOptions: {
        prefix: true,
        fuzzy: 0.2,
      },
    });
    setSearchIndex(index);
  }, []);

  // Debounce the global filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedGlobalFilter(globalFilter);
      if (globalFilter.trim() !== '') {
        setSearchTime(null);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [globalFilter]);

  // Fetch courses data from API
  useEffect(() => {
    const fetchCourses = async () => {
      // Check if courses are cached in memory (avoids sessionStorage quota issues)
      const cachedData = window.harvardCoursesCache;
      const CURRENT_VERSION = '1.1';
      
      if (cachedData && cachedData.version === CURRENT_VERSION) {
        const cacheAge = Date.now() - cachedData.timestamp;
        const maxAge = 60 * 60 * 1000; // 1 hour
        
        if (cacheAge < maxAge) {
          console.log('✅ Using memory cached courses data:', { 
            coursesCount: cachedData.courses?.length, 
            total: cachedData.total,
            cacheAgeMinutes: Math.round(cacheAge / 60000)
          });
          
          setTotalCount(cachedData.total || 0);
          setCourses(cachedData.courses || []);
          setLoading(false);
          setIsDeduplicationDone(false);
          
          logDebug('CACHE_HIT', {
            cachedCoursesCount: cachedData.courses?.length || 0,
            cacheVersion: cachedData.version,
            cacheAgeMinutes: Math.round(cacheAge / 60000),
            message: 'Loaded from memory cache'
          });
          return;
        } else {
          console.log('🔄 Cache expired, fetching fresh data');
          delete window.harvardCoursesCache;
        }
      }
      
      // Fetch fresh data
      setLoading(true);
      
      try {
        console.log('Fetching fresh courses data...');
        const response = await axios.get(createApiUrl('api/courses'));
        const { courses: coursesData, total } = response.data;
        
        console.log('Received courses data:', { coursesCount: coursesData?.length, total });
        
        // Cache the response in memory (sessionStorage too small for full dataset)
        window.harvardCoursesCache = {
          courses: coursesData,
          total: total,
          version: '1.1',
          timestamp: Date.now()
        };
        console.log('✅ Courses cached in memory:', { coursesCount: coursesData?.length, total });
        
        setTotalCount(total || 0);
        setCourses(coursesData || []);
        setIsDeduplicationDone(false); // Reset deduplication flag for new data
        
        logDebug('API_FETCH', {
          rawCoursesReceived: coursesData?.length || 0,
          totalCount: total,
          sampleCourse: coursesData?.[0],
          duplicateCheck: {
            firstCourseId: coursesData?.[0]?.course_id,
            firstClassNumber: coursesData?.[0]?.class_number,
          }
        });
        
      } catch (error) {
        console.error('Error fetching courses:', error);
        setCourses([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);


  // Re-index when courses data changes - FIXED: Proper deduplication without state corruption
  useEffect(() => {
    if (courses.length > 0 && !isDeduplicationDone) {
      console.log('Building search index...');
      const startTime = performance.now();
      
      // Deduplicate courses using compound key strategy
      const uniqueCoursesMap = new Map();
      const duplicateTracker = {};
      
      courses.forEach((course) => {
        const key = `${course.course_id}-${course.class_number}`;
        if (!uniqueCoursesMap.has(key)) {
          uniqueCoursesMap.set(key, course);
          duplicateTracker[key] = 1;
        } else {
          duplicateTracker[key] = (duplicateTracker[key] || 1) + 1;
        }
      });
      
      const deduplicatedCourses = Array.from(uniqueCoursesMap.values());
      
      logDebug('DEDUPLICATION', {
        originalCount: courses.length,
        uniqueCount: deduplicatedCourses.length,
        duplicatesRemoved: courses.length - deduplicatedCourses.length,
        duplicateKeys: Object.entries(duplicateTracker).filter(([key, count]) => count > 1),
        sampleDuplicate: Object.entries(duplicateTracker).find(([key, count]) => count > 1)?.[0]
      });
      
      // Note: Search index building moved to separate effect to handle timing issues
      
      // CRITICAL FIX: Set unique courses separately, don't modify courses state
      setUniqueCourses(deduplicatedCourses);
      setFilteredCourses(deduplicatedCourses); // Initialize with all unique courses
      setCurrentPage(1);
      setIsDeduplicationDone(true);
    }
  }, [courses, isDeduplicationDone]);

  // Separate effect to build search index when both deduplication is done and searchIndex is ready
  useEffect(() => {
    if (isDeduplicationDone && searchIndex && uniqueCourses.length > 0) {
      console.log('🔍 Building search index after deduplication completion');
      const startTime = performance.now();
      
      // Clear and rebuild index with unique courses
      searchIndex.removeAll();
      searchIndex.addAll(uniqueCourses);
      
      console.log(`🚀 Search index rebuilt in ${performance.now() - startTime}ms for ${uniqueCourses.length} unique courses`);
    }
  }, [isDeduplicationDone, searchIndex, uniqueCourses]);

  // Apply filters function
  const applyFilters = useCallback(
    (coursesToFilter) => {
      return coursesToFilter.filter((course) => {
        // Term filter
        if (
          activeFilters.term.length > 0 &&
          course.term &&
          !activeFilters.term.includes(course.term)
        ) {
          return false;
        }

        // Subject filter
        if (activeFilters.subject.length > 0 && course.class_tag) {
          const subjectCode = course.class_tag.split(' ')[0];
          if (!activeFilters.subject.includes(subjectCode)) {
            return false;
          }
        }

        // Q ratings filter
        if (
          activeFilters.showOnlyQRatings &&
          (course.overall_score_course_mean === null ||
            course.overall_score_course_mean === undefined)
        ) {
          return false;
        }

        // Scheduled courses filter - FIXED: Validate array type
        if (activeFilters.showOnlyScheduled) {
          if (!course.start_times || 
              !Array.isArray(course.start_times) ||
              !course.start_times.some((time) => time !== null && time !== 'N/A' && time !== undefined)) {
            return false;
          }
        }

        // Day filter
        if (Object.values(activeFilters.days).some((day) => day === true)) {
          const dayKeys = Object.keys(activeFilters.days);
          const activeDays = dayKeys.filter((day) => activeFilters.days[day]);

          if (activeDays.length > 0 && course.start_times) {
            // Parse start_times if it's a JSON string
            let startTimes = course.start_times;
            if (typeof startTimes === 'string') {
              try {
                startTimes = JSON.parse(startTimes);
              } catch (e) {
                startTimes = [];
              }
            }

            if (Array.isArray(startTimes)) {
              const hasMeetingDay = activeDays.some((day) => {
              let dayIndex = -1;
              switch (day) {
                case 'M':
                  dayIndex = 1;
                  break;
                case 'T':
                  dayIndex = 2;
                  break;
                case 'W':
                  dayIndex = 3;
                  break;
                case 'Th':
                  dayIndex = 4;
                  break;
                case 'F':
                  dayIndex = 5;
                  break;
                default:
                  dayIndex = -1;
                  break;
              }

                return (
                  dayIndex >= 0 &&
                  dayIndex < startTimes.length &&
                  startTimes[dayIndex] !== null &&
                  startTimes[dayIndex] !== 'N/A' &&
                  startTimes[dayIndex] !== undefined
                );
              });

              if (!hasMeetingDay) return false;
            } else {
              // start_times not an array even after parsing
              return false;
            }
          } else if (activeDays.length > 0 && !course.start_times) {
            return false;
          }
        }

        return true;
      });
    },
    [activeFilters]
  );

  // Apply filters and search - FIXED: Use uniqueCourses and preserve relevance
  useEffect(() => {
    if (!loading && searchIndex && uniqueCourses.length > 0) {
      const startTime = performance.now();

      logDebug('FILTER_START', {
        uniqueCoursesCount: uniqueCourses.length,
        searchQuery: debouncedGlobalFilter,
        activeFilters: activeFilters,
        hasSearchIndex: !!searchIndex
      });

      // CRITICAL FIX: Use uniqueCourses instead of courses
      let coursesToFilter = uniqueCourses;
      if (debouncedGlobalFilter.trim() !== '') {
        // Check if the query contains quoted phrases
        const hasQuotes = debouncedGlobalFilter.includes('"');
        let results = [];

        if (hasQuotes) {
          // Extract phrases in quotes
          const quoteRegex = /"([^"]*)"/g;
          const matches = Array.from(debouncedGlobalFilter.matchAll(quoteRegex));
          
          // Get the quoted phrases
          const quotedPhrases = matches.map((match) => match[1]);

          // Get the remaining search terms (outside quotes)
          let remainingQuery = debouncedGlobalFilter;
          quotedPhrases.forEach((phrase) => {
            remainingQuery = remainingQuery.replace(`"${phrase}"`, '');
          });
          remainingQuery = remainingQuery.trim();

          // Remove stop words from non-quoted parts
          let filteredQuery = '';
          if (remainingQuery) {
            filteredQuery = remainingQuery
              .split(/\s+/)
              .filter((word) => !STOP_WORDS.has(word.toLowerCase()))
              .join(' ');
          }

          // First get all potential matches using filtered non-quoted terms
          const potentialMatches = filteredQuery
            ? searchIndex.search(filteredQuery)
            : searchIndex.search('*');

          // Then filter to ensure quoted phrases are exact matches
          results = potentialMatches.filter((result) => {
            return quotedPhrases.every((phrase) => {
              const phraseLC = phrase.toLowerCase();
              const classTag = (result.class_tag || '').toLowerCase();
              const className = (result.class_name || '').toLowerCase();
              const schedule = (result.schedule || '').toLowerCase();
              const instructors = Array.isArray(result.instructors)
                ? result.instructors.map((i) => i.toLowerCase()).join(' ')
                : '';

              return (
                classTag.includes(phraseLC) ||
                className.includes(phraseLC) ||
                schedule.includes(phraseLC) ||
                instructors.includes(phraseLC)
              );
            });
          });
        } else {
          // For regular search, filter out stop words
          const filteredQuery = debouncedGlobalFilter
            .split(/\s+/)
            .filter(
              (word) => !STOP_WORDS.has(word.toLowerCase()) && word.length > 0
            )
            .join(' ');

          // Only search if there are non-stop words
          if (filteredQuery) {
            results = searchIndex.search(filteredQuery);
          } else {
            results = [];
          }
        }

        // CRITICAL FIX: Map results preserving MiniSearch relevance order
        coursesToFilter = results
          .map((result, index) => {
            const courseId = result.id;
            const matchedCourse = uniqueCourses.find(
              (course) => `${course.course_id}-${course.class_number}` === courseId
            );
            if (matchedCourse) {
              return {
                ...matchedCourse,
                _searchScore: result.score,
                _searchRank: index
              };
            }
            return null;
          })
          .filter(Boolean)
          .sort((a, b) => a._searchRank - b._searchRank); // Preserve MiniSearch order

        logDebug('SEARCH_RESULTS', {
          searchQuery: debouncedGlobalFilter,
          totalResults: results.length,
          mappedResults: coursesToFilter.length,
          topResults: coursesToFilter.slice(0, 3).map(c => ({
            class_tag: c.class_tag,
            class_name: c.class_name,
            searchScore: c._searchScore,
            searchRank: c._searchRank
          }))
        });
      }

      // Apply filters to search results or all courses
      let filteredResults = applyFilters(coursesToFilter);

      logDebug('FILTER_APPLIED', {
        beforeFiltering: coursesToFilter.length,
        afterFiltering: filteredResults.length,
        filtersActive: {
          term: activeFilters.term.length > 0 ? activeFilters.term : null,
          subject: activeFilters.subject.length > 0 ? activeFilters.subject : null,
          qRatings: activeFilters.showOnlyQRatings,
          scheduled: activeFilters.showOnlyScheduled,
          days: Object.entries(activeFilters.days).filter(([day, active]) => active).map(([day]) => day)
        },
        sampleFilteredCourse: filteredResults[0] ? {
          class_tag: filteredResults[0].class_tag,
          class_name: filteredResults[0].class_name,
          term: filteredResults[0].term
        } : null
      });

      // Apply sorting
      if (activeFilters.sortByWorkload !== 'none') {
        filteredResults = [...filteredResults].sort((a, b) => {
          const aHours =
            a.mean_hours === null || a.mean_hours === undefined
              ? 999
              : a.mean_hours;
          const bHours =
            b.mean_hours === null || b.mean_hours === undefined
              ? 999
              : b.mean_hours;

          if (activeFilters.sortByWorkload === 'low-to-high') {
            return aHours - bHours; // GEM: Low to high
          } else {
            return bHours - aHours; // PAIN: High to low
          }
        });
      } else if (activeFilters.sortByQScore !== 'none') {
        filteredResults = [...filteredResults].sort((a, b) => {
          const aScore =
            a.overall_score_course_mean === null ||
            a.overall_score_course_mean === undefined
              ? -1
              : a.overall_score_course_mean;
          const bScore =
            b.overall_score_course_mean === null ||
            b.overall_score_course_mean === undefined
              ? -1
              : b.overall_score_course_mean;

          if (activeFilters.sortByQScore === 'high-to-low') {
            return bScore - aScore; // GOAT: High to low
          } else {
            return aScore - bScore; // BUNS: Low to high
          }
        });
      }

      setFilteredCourses(filteredResults);
      setCurrentPage(1); // Reset to first page on filter/search change

      const endTime = performance.now();
      setSearchTime(endTime - startTime);
      
      logDebug('PIPELINE_COMPLETE', {
        finalResultCount: filteredResults.length,
        processingTime: `${(endTime - startTime).toFixed(2)}ms`,
        topResults: filteredResults.slice(0, 3).map(c => c.class_tag)
      });
    }
  }, [
    debouncedGlobalFilter,
    activeFilters,
    uniqueCourses, // CRITICAL FIX: Use uniqueCourses instead of courses
    loading,
    searchIndex,
    applyFilters,
  ]);

  // Extract unique subjects from uniqueCourses - FIXED
  useEffect(() => {
    if (uniqueCourses.length > 0) {
      const uniqueSubjects = new Set();
      const subjectMap = new Map();

      const deptSubjectMap = subjectMappingsData[0]?.dept_subject_map || {};

      uniqueCourses.forEach((course) => {
        if (course.class_tag) {
          const parts = course.class_tag.split(' ');
          if (parts.length > 0) {
            uniqueSubjects.add(parts[0]);
          }
        }
      });

      uniqueSubjects.forEach((subject) => {
        const fullName = deptSubjectMap[subject];
        if (fullName) {
          subjectMap.set(subject, fullName);
        }
      });

      setSubjectOptions(Array.from(uniqueSubjects).sort());
      setSubjectMapping(subjectMap);
      
      logDebug('SUBJECTS_EXTRACTED', {
        totalSubjects: uniqueSubjects.size,
        sampleSubjects: Array.from(uniqueSubjects).slice(0, 5),
        mappedSubjects: Array.from(subjectMap.entries()).slice(0, 3)
      });
    }
  }, [uniqueCourses]); // FIXED: Use uniqueCourses

  // Add course to calendar
  const handleAddToCal = async (course) => {
    try {
      const result = await addCourse(course.class_tag);
      if (result.success) {
        showToast(`Added ${course.class_tag} to calendar`, 'success');
        setShowCalendarWidget(true);
      } else {
        if (result.error?.response?.data?.message?.includes('already exists')) {
          showToast('Course already in calendar', 'info');
        } else {
          showToast('Failed to add to calendar', 'error');
        }
      }
    } catch (error) {
      console.error('Error adding to calendar:', error);
      showToast('Failed to add to calendar', 'error');
    }
  };

  // Handle filter changes - FIXED: Use useCallback to prevent infinite loops
  const handleFilterChange = useCallback((filters) => {
    setActiveFilters(filters);
  }, []);

  // Calculate courses to display for current page
  const totalFilteredPages = Math.ceil(filteredCourses.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const coursesToDisplay = filteredCourses.slice(startIndex, endIndex);

  // Pagination handlers
  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalFilteredPages));
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleSearchChange = (e) => {
    setGlobalFilter(e.target.value);
  };

  // Debug panel toggle
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  useEffect(() => {
    // Auto-show debug panel if there are issues
    const hasIssues = filteredCourses.length === 0 && !loading && uniqueCourses.length > 0;
    if (hasIssues && !showDebugPanel) {
      setShowDebugPanel(true);
      console.log('🚨 Auto-showing debug panel due to potential issues');
    }
  }, [filteredCourses.length, loading, uniqueCourses.length, showDebugPanel]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-100">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-800">
                Course Search
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-dark-600">
                Search and filter through {totalCount} Harvard courses
              </p>
            </div>
            <div>
              <button
                onClick={() => setShowDebugPanel(!showDebugPanel)}
                className="text-xs bg-gray-200 dark:bg-dark-300 px-2 py-1 rounded"
              >
                {showDebugPanel ? 'Hide' : 'Show'} Debug
              </button>
            </div>
          </div>
        </div>

        {/* Debug Panel */}
        {showDebugPanel && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <h3 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">Debug Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-yellow-700 dark:text-yellow-300">
              <div>
                <strong>Raw Courses:</strong> {courses.length}
              </div>
              <div>
                <strong>Unique Courses:</strong> {uniqueCourses.length}
              </div>
              <div>
                <strong>Filtered Results:</strong> {filteredCourses.length}
              </div>
              <div>
                <strong>Search Time:</strong> {searchTime ? `${(searchTime / 1000).toFixed(3)}s` : 'N/A'}
              </div>
            </div>
            {debugInfo.DEDUPLICATION && (
              <div className="mt-2 text-xs text-yellow-700 dark:text-yellow-300">
                <strong>Duplicates Removed:</strong> {debugInfo.DEDUPLICATION.duplicatesRemoved}
                {debugInfo.DEDUPLICATION.duplicateKeys?.length > 0 && (
                  <span className="ml-2">
                    (Sample: {debugInfo.DEDUPLICATION.sampleDuplicate})
                  </span>
                )}
              </div>
            )}
            {debugInfo.FILTER_APPLIED && (
              <div className="mt-2 text-xs text-yellow-700 dark:text-yellow-300">
                <strong>Active Filters:</strong> {
                  Object.entries(debugInfo.FILTER_APPLIED.filtersActive)
                    .filter(([key, value]) => value && (Array.isArray(value) ? value.length > 0 : true))
                    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                    .join(' | ') || 'None'
                }
              </div>
            )}
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              className="input w-full text-lg py-3 pl-4 pr-4"
              placeholder="Search courses by name, code, or instructor..."
              ref={searchInputRef}
              value={globalFilter}
              onChange={handleSearchChange}
            />
          </div>
          {searchTime !== null && debouncedGlobalFilter.trim() !== '' && (
            <div className="text-sm text-gray-500 dark:text-dark-600 mt-2">
              {filteredCourses.length} results in {(searchTime / 1000).toFixed(3)}s
            </div>
          )}
        </div>

        {/* Course Filters */}
        <div className="mb-6">
          <CourseFilters
            onFilterChange={handleFilterChange}
            subjectOptions={subjectOptions}
            subjectMapping={subjectMapping}
            termOptions={termOptions}
          />
        </div>

        {/* Course Table */}
        <div className="bg-white dark:bg-dark-200 rounded-lg shadow">
          <CourseTable
            courses={coursesToDisplay}
            isLoading={loading}
            globalFilter={debouncedGlobalFilter}
            onAddToCal={handleAddToCal}
          />
          
          {/* Pagination */}
          {!loading && filteredCourses.length > 0 && (
            <div className="flex items-center justify-center space-x-4 py-4 border-t border-gray-200 dark:border-dark-400">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700 dark:text-dark-700">
                Page {currentPage} of {totalFilteredPages} ({filteredCourses.length} results)
              </span>
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalFilteredPages}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}

          {/* No Results */}
          {!loading && filteredCourses.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-dark-600">
              No courses found matching your search criteria.
            </div>
          )}
        </div>
      </div>

      {/* Floating Calendar Widget */}
      <FloatingCalendarWidget
        isVisible={showCalendarWidget}
        onClose={() => setShowCalendarWidget(false)}
      />
    </div>
  );
}

export default Courses;